import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { formatAddress } from '../server/google-cloud/format-address.js';
import { geocodeAddress } from '../server/google-cloud/geocodeAddress.js';
import { RouteInfo } from './RouteInfo.js';
import { RouteTicket, RouteTicketAssigned } from './RouteTicket.js';

/**
 * - `full-simulation`: manual full simulation without writing back to otrs
 * - `automatic-dry-run`: automatic simulation without writing back to otrs
 * - `automatic-run`: automatic simulation **with** writing back to otrs
 * - `manual-dry-run`: manual simulation over set of ticketIDs without writing back to otrs
 * - `manual-run`: manual simulation over set of ticketIDs **with** writing back to otrs
 */
export type simulationType = 'automatic-dry-run' | 'automatic-run' | 'manual-dry-run' | 'manual-run';

@Entity()
export class FieldEngineerIntervention extends AdornisEntity {
  static override _class = 'FieldEngineerIntervention';

  static override resolveID(entity: any) {
    return (entity._id ??= A.getGloballyUniqueID());
  }

  constructor(doc: PartialEntityData<FieldEngineerIntervention>) {
    super(doc);
  }

  @Field(_type => ID) _id!: string;
  @Field(_type => Date) startTime: Maybe<Date>;
  @Field(_type => Date) endTime: Maybe<Date>;
  @Field(_type => String) location: Maybe<string>;
  @Field(_type => Float) lat: Maybe<number>;
  @Field(_type => Float) lng: Maybe<number>;
}

@Entity()
export class AvailableFieldEngineer extends AdornisEntity {
  static override _class = 'AvailableFieldEngineer';

  static override resolveID(entity: any) {
    return (entity._id ??= A.getGloballyUniqueID());
  }

  constructor(doc: PartialEntityData<AvailableFieldEngineer>) {
    super(doc);
  }

  @Field(_type => ID) _id!: string;
  @Field(_type => String) id: Maybe<string>;
  @Field(_type => String) name: Maybe<string>;
  @Field(_type => String) city: Maybe<string>;
  @Field(_type => String) postalCode: Maybe<string>;
  @Field(_type => String) country: Maybe<string>;
  @Field(_type => Date) startTime: Maybe<Date>;
  @Field(_type => Date) endTime: Maybe<Date>;
  @Field(_type => Float) lat: Maybe<number>;
  @Field(_type => Float) lng: Maybe<number>;
  @Field(_type => String) depotID: Maybe<string>;
  @Field(_type => [FieldEngineerIntervention]) interventions: Maybe<FieldEngineerIntervention[]>;

  // those fields are used for profitability calculations and represent the full availability of the engineer
  // whereas the startTime and endTime fields are the practical times for the route planning
  @Field(_type => Date) theoreticalStartTime: Maybe<Date>;
  @Field(_type => Date) theoreticalEndTime: Maybe<Date>;

  @Field(_type => [String]) preAssignedTicketIDs: Maybe<string[]>;
}

@Entity()
export class RouteOptimizationResult extends MongoEntity {
  static override _collectionName = 'route-plannings';
  static override _class = 'RouteOptimizationResult';

  @Field(_type => Date) createdAt: Maybe<Date>;
  @Field(_type => String) type: Maybe<'Dell'>;
  @Field(_type => [RouteInfo]) routes!: RouteInfo[];
  @Field(_type => [RouteTicket]) fourHourSlaTickets: Maybe<RouteTicket[]>;
  @Field(_type => [RouteTicket]) imacTickets: Maybe<RouteTicket[]>;
  @Field(_type => [RouteTicketAssigned]) fixedFETickets: Maybe<RouteTicketAssigned[]>;
  @Field(_type => [RouteTicket], {
    resolve: function (this: RouteOptimizationResult) {
      return async (_gqlFields: BaseQLSelectionSet<RouteTicket>) => {
        const scheduledTickets = this.routes.flatMap(route =>
          (route.stops ?? []).filter(stop => stop.type === 'ticket').map(stop => stop.ticketId),
        );
        return Promise.all(
          (this.ticketsToSchedule ?? [])
            .concat(this.ticketsWithFETomorrow ?? [])
            .filter(ticket => !scheduledTickets.includes(ticket.id))
            .map(async ticket => {
              if (!ticket.address || !ticket.zip || !ticket.city) return ticket;
              if (ticket.lat && ticket.lng) return ticket;

              try {
                const coords = await geocodeAddress(formatAddress(ticket));
                if (!coords) return ticket as unknown as RouteTicket;

                ticket.lat = coords.latitude;
                ticket.lng = coords.longitude;

                return ticket;
              } catch {
                return ticket as unknown as RouteTicket;
              }
            }),
        );
      };
    },
  })
  unscheduledTickets!: Maybe<RouteTicket[]>;
  @Field(_type => [RouteTicket]) ticketsIgnored!: Maybe<RouteTicket[]>;
  @Field(_type => [RouteTicket]) ticketsToSchedule!: Maybe<RouteTicket[]>;
  @Field(_type => [RouteTicketAssigned]) ticketsWithFETomorrow!: Maybe<RouteTicketAssigned[]>;
  @Field(_type => Int, {
    resolve: function (this: RouteOptimizationResult) {
      return () => {
        const ticketIdsToSchedule = new Set((this.ticketsToSchedule ?? []).map(ticket => ticket.id).filter(Boolean));
        if (ticketIdsToSchedule.size === 0) return 0;

        const assignedTicketIds = new Set<string>();
        for (const route of this.routes ?? []) {
          if (!route?.vehicleId) continue;
          for (const stop of route.stops ?? []) {
            if (stop?.type !== 'ticket') continue;
            const ticketId = stop.ticketId;
            if (!ticketId) continue;
            if (!ticketIdsToSchedule.has(ticketId)) continue;
            assignedTicketIds.add(ticketId);
          }
        }

        return assignedTicketIds.size;
      };
    },
  })
  numNewlyAssignedTickets: Maybe<number>;
  @Field(_type => Boolean, { default: v => v ?? false }) writtenBackToOTRS: Maybe<boolean>;
  @Field(_type => String) simulationType: Maybe<
    'full-simulation' | 'automatic-dry-run' | 'automatic-run' | 'manual-dry-run' | 'manual-run' | 'checksum'
  >;
  @Field(_type => [AvailableFieldEngineer]) fieldEngineersAvailable!: Maybe<AvailableFieldEngineer[]>;
  @Field(_type => [AvailableFieldEngineer], {
    resolve: function (this: RouteOptimizationResult) {
      return () => {
        return Promise.all(
          (this.fieldEngineersAvailable ?? []).map(async engineer => {
            if (!engineer.postalCode || !engineer.country) return engineer;

            try {
              const coords = await geocodeAddress(`${engineer.postalCode}, ${engineer.country}`);
              if (!coords) return engineer as unknown as AvailableFieldEngineer;

              engineer.lat = coords.latitude;
              engineer.lng = coords.longitude;

              return engineer;
            } catch {
              return engineer as unknown as AvailableFieldEngineer;
            }
          }),
        );
      };
    },
  })
  fieldEngineersAvailableWithCoords: Maybe<AvailableFieldEngineer[]>;
  @Field(_type => Int) numTicketsInFilter: Maybe<number>;
  @Field(_type => [AvailableFieldEngineer]) partnerFieldEngineersAvailable!: Maybe<AvailableFieldEngineer[]>;

  getUnassignedTickets() {
    return this.unscheduledTickets ?? [];
  }

  override toString(): string {
    const lines: string[] = [];
    lines.push('\n╔═══════════════════════════════════════════════════════════════════════════════════════════╗');
    lines.push('║                           ROUTE OPTIMIZATION RESULT                                       ║');
    lines.push('╚═══════════════════════════════════════════════════════════════════════════════════════════╝\n');

    // Ausgabe aller Routes
    if (this.routes && this.routes.length > 0) {
      for (const route of this.routes) {
        lines.push(route.toString());
      }
    } else {
      lines.push('Keine Routen gefunden.\n');
    }

    // Ausgabe der nicht zugewiesenen Tickets
    const unassignedTickets = this.getUnassignedTickets();
    if (unassignedTickets.length > 0) {
      lines.push('\n╔═══════════════════════════════════════════════════════════════════════════════════════════╗');
      lines.push('║                           ⚠️  NICHT ZUGEWIESENE TICKETS                                   ║');
      lines.push('╚═══════════════════════════════════════════════════════════════════════════════════════════╝\n');

      for (const ticket of unassignedTickets) {
        lines.push(
          `🔴 ${ticket.number || ticket.id} - ${ticket.address}, ${ticket.zip} ${ticket.city} (Type: ${ticket.type})`,
        );
      }
      lines.push('');
    }

    return lines.join('\n');
  }

  constructor(doc: PartialEntityData<RouteOptimizationResult>) {
    super(doc);
  }
}
